/**
 * @classdesc Slightly modified stack structure used to coordinate the eventual multiple dialogs in
 * a page.
 *
 * @class DialogsStack
 * @version 1.0.0
 * @path
 *
 * @see [Stack data type]{@link https://en.wikipedia.org/wiki/Stack_(abstract_data_type)}
 */
class DialogsStack {
	constructor() {
		this.stack = [];
	}

	/**
	 * Gives the last element without removing it
	 *
	 * @returns {Dialog|null} return the last element on the stack or null when it is empty
	 * @memberof DialogsStack
	 */
	getLast() {
		return this.stack.length ? this.stack[this.stack.length - 1] : null;
	}

	/**
	 * Gives the last modal Dialog in the stack without removing it
	 *
	 * @returns {Dialog|null} return the last modal element on the stack or null when it is empty or
	 * no modal elements are in the stack
	 * @memberof DialogsStack
	 */
	getLastModal() {
		for (let i = this.stack.length - 1; i >= 0; i--) {
			if (this.stack[i].isModal()) {
				return this.stack[i];
			}
		}
		return null;
	}

	/**
	 * Gives a copy of the internal stack
	 *
	 * @returns {Dialog[]}
	 * @memberof DialogsStack
	 */
	getAll() {
		return this.stack.slice(0);
	}

	/**
	 * Gives a list of all the modal Dialog in the stack
	 *
	 * @returns {Dialog[]}
	 * @memberof DialogsStack
	 */
	getAllModals() {
		return this.getAll().filter((dialog) => dialog.isModal());
	}

	/**
	 * Push a Dialog in the stack
	 *
	 * @param {Dialog} dialog the dialog added at the end
	 * @returns {int} the new size of the stack
	 * @memberof DialogsStack
	 */
	push(dialog) {
		this.stack.push(dialog);
		return this.stack.length;
	}

	/**
	 * remove the last element and returns it, if the stack is not empty
	 *
	 * @returns {Dialog|undefined} undefined when called on an empty stack
	 * @memberof DialogsStack
	 */
	pop() {
		if (this.stack.length) {
			return this.stack.pop();
		}
		return undefined;
	}

	/**
	 * Removes a specific Dialog, if present, from any position in the stack
	 *
	 * @param {Dialog} dialog the Dialog to be removed
	 * @returns {boolean} true when the element is found and removed, false otherwise
	 * @memberof DialogsStack
	 */
	remove(dialog) {
		const index = this.stack.indexOf(dialog);
		if (index >= 0) {
			this.stack.splice(index, 1);
			return true;
		}
		return false;
	}

	/**
	 * Get the size of the stack
	 * @type {number}
	 * @readonly
	 *
	 * @memberof DialogsStack
	 */
	get length() {
		return this.stack.length;
	}
}

/**
 * The single instance shared among all the Dialogs
 * @type DialogsStack
 */
const dialogsStack = new DialogsStack();

export {dialogsStack, DialogsStack};
